// src/common/components/cartModal/components/checkout-stage/components/paymentMethodSelector/PaymentMethodSelector.tsx
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styles from "./PaymentMethodSelector.module.scss";
import { PaymentMethod, DeliveryType } from "../../../../types/enums";
import { useTheme } from "../../../../hooks/useTheme";
import { getPaymentMethodIcon } from "../../../../utils/functions/enumToIcon";
import { getPaymentMethodText } from "../../../../utils/functions/enumToText";
import SelectableCard from "../selectableCard/SelectableCard";
import {
  selectIsExecutor,
  selectIsAdmin,
} from "../../../../../store/slices/executorSlice";
import { selectCurrentCluster } from "../../../../../store/slices/currentClusterSlice";

interface PaymentMethodSelectorProps {
  paymentMethod: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  deliveryType: DeliveryType;
}

export default function PaymentMethodSelector({
  paymentMethod,
  onSelect,
  deliveryType,
}: PaymentMethodSelectorProps) {
  const { theme } = useTheme();
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const isManager = isExecutor || isAdmin;
  const currentCluster = useSelector(selectCurrentCluster);

  const payment = currentCluster?.payment;
  const isDelivery = deliveryType === DeliveryType.DELIVERY;

  const paymentOptions = useMemo(() => {
    const options = [];

    const cashEnabled = isDelivery
      ? payment?.cashOnDelivery
      : payment?.cashOnPickup;
    if (cashEnabled) {
      options.push({
        method: PaymentMethod.CASH_ON_DELIVERY,
        icon: getPaymentMethodIcon(PaymentMethod.CASH_ON_DELIVERY),
        label: getPaymentMethodText(PaymentMethod.CASH_ON_DELIVERY),
      });
    }

    const cardEnabled = isDelivery
      ? payment?.cardOnDelivery
      : payment?.cardOnPickup;
    if (cardEnabled) {
      options.push({
        method: PaymentMethod.CARD_ON_DELIVERY,
        icon: getPaymentMethodIcon(PaymentMethod.CARD_ON_DELIVERY),
        label: getPaymentMethodText(PaymentMethod.CARD_ON_DELIVERY),
      });
    }

    const onlineEnabled = isDelivery
      ? payment?.onlineOnDelivery
      : payment?.onlineOnPickup;
    if (!isManager && onlineEnabled) {
      options.push({
        method: PaymentMethod.ONLINE,
        icon: getPaymentMethodIcon(PaymentMethod.ONLINE),
        label: getPaymentMethodText(PaymentMethod.ONLINE),
      });
    }

    return options;
  }, [payment, isDelivery, isManager]);

  if (paymentOptions.length === 0) return null;

  return (
    <div className={styles.container}>
      <h3 className={styles.title} style={{ color: theme.text }}>
        Способ оплаты
      </h3>
      <div className={styles.optionsContainer}>
        {paymentOptions.map((option) => (
          <SelectableCard
            key={option.method}
            isSelected={paymentMethod === option.method}
            onSelect={() => onSelect(option.method)}
            icon={option.icon}
            label={option.label}
            showCheckmark={true}
          />
        ))}
      </div>
    </div>
  );
}
